// src/components/DevicePanel/Map/MarkerManager/index.tsx

import React, { memo, useEffect } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { ROUTES } from "../../../config/routes";
import { Marker, Tooltip } from "react-leaflet";
import { Icon } from "leaflet";
import MapIconPin from "../../../assets/icons/MapIconPin.svg";
import MapIconPinWarning from "../../../assets/icons/MapIconPinWarning.svg";
import ArchiveBoxIcon from "@heroicons/react/24/outline/ArchiveBoxIcon";
import { ReactComponent as MosquitoIconSvg } from "../../../assets/icons/MosquitoVector.svg";
import "./index.css";
import { Typography, Badge } from "antd";
import { MarkerData } from "../../../types/components/map";

const { Text } = Typography;

const DEFAULT_TRAP_ICON = new Icon({
  iconUrl: MapIconPin,
  iconAnchor: [15, 40],
  className: "w-10 h-10 translate-y-[20px] z-1000",
});

const WARNING_TRAP_ICON = new Icon({
  iconUrl: MapIconPinWarning,
  iconAnchor: [15, 40],
  className: "w-10 h-10 translate-y-[20px] z-1000",
});

const TrapMarker = memo(({ marker }: { marker: MarkerData }) => {
  const navigate = useNavigate();

  useEffect(() => {
    let observers: MutationObserver[] = [];
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll(
        ".leaflet-tooltip-top.map-marker, .leaflet-tooltip-bottom.map-marker",
      );
      elements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const applyTransform = () => {
          const currentTransform = htmlEl.style.transform;
          const halfWidth = htmlEl.offsetWidth / 2;

          if (!currentTransform) return;

          const isBottom = htmlEl.classList.contains("leaflet-tooltip-bottom");
          const expectedTranslate = isBottom
            ? ` translateX(${halfWidth}px)`
            : ` translateX(-${halfWidth}px)`;

          if (!currentTransform.includes(expectedTranslate) && halfWidth > 0) {
            const cleanTransform = currentTransform.replace(
              / translateX\([^)]+\)/g,
              "",
            );
            htmlEl.style.transform = `${cleanTransform}${expectedTranslate}`;
          }
        };

        // Apply immediately
        applyTransform();

        // Listen for style updates from Leaflet (e.g. during map drag)
        const observer = new MutationObserver(() => applyTransform());
        observer.observe(htmlEl, {
          attributes: true,
          attributeFilter: ["style"],
        });
        observers.push(observer);
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      observers.forEach((obs) => obs.disconnect());
    };
  }, [marker.warning]);

  return (
    <Marker
      position={marker.position}
      zIndexOffset={700}
      icon={marker.warning ? WARNING_TRAP_ICON : DEFAULT_TRAP_ICON}
      eventHandlers={{
        click: () => {
          if (marker.id) {
            navigate(generatePath(ROUTES.location_detail, { id: marker.id }));
          }
        },
      }}
    >
      <Tooltip
        key={`${marker?.warning}`}
        permanent
        className={`map-marker ${marker?.warning ? "warning" : ""}`}
        direction={"auto"}
      >
        <div
          className="px-2 py-2 flex items-center min-w-[12rem] gap-3 pr-6"
          style={{ cursor: "pointer" }}
        >
          <div className="flex flex-col flex-grow justify-center">
            <div className="flex items-center gap-2">
              {marker.markerType === "device" && (
                <ArchiveBoxIcon
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: "#0c4a6e" }}
                />
              )}
              <Text
                className="marker-display-name m-0"
                style={{
                  color: "#0c4a6e",
                  fontSize: "1.2rem",
                  lineHeight: "1.2",
                }}
              >
                {marker.displayName}
              </Text>
            </div>
            {marker.markerType === "device" && marker.gps && (
              <Text
                className="marker-gps-text m-0 mt-1 pl-7"
                style={{
                  color: "#0c4a6e",
                  fontSize: "0.9rem",
                  lineHeight: "1.2",
                }}
              >
                {marker.gps.lat.toFixed(4)}, {marker.gps.lon.toFixed(4)}
              </Text>
            )}
          </div>

          <div className="flex items-center gap-2">
            {marker.maintainCount ? (
              <Badge
                count={marker.maintainCount}
                offset={[0, 2]}
                color="#f4c000"
              >
                <ArchiveBoxIcon
                  className="w-5 h-5 trapIcon warning"
                  style={{ color: "#f4c000" }}
                />
              </Badge>
            ) : null}
            {marker.detectionsCount ? (
              <Badge
                count={marker.detectionsCount}
                color="#f4c000"
                offset={[0, 2]}
              >
                <MosquitoIconSvg
                  width={20}
                  height={20}
                  className="trapIcon"
                  fill="#f4c000"
                />
              </Badge>
            ) : null}
          </div>
        </div>
      </Tooltip>
    </Marker>
  );
});

export default TrapMarker;
